/**
 * @fileoverview Module for `src/admin/AdminUserOverrides.js`.
 */

import axios from 'axios';
import propTypes from 'prop-types';
import React, { Component } from 'react';

import i18n from '../locales/strings.json';

/**
 * Admin controls for user-level overrides such as letter adoption caps.
 */
class AdminUserOverrides extends Component {
  static get propTypes() {
    return {
      auth: propTypes.object.isRequired,
      locale: propTypes.string,
      user: propTypes.object.isRequired,
      onUserUpdated: propTypes.func.isRequired,
    };
  }

  /**
   * Initializes locale and override expansion state.
   *
   * @param {Object} props - Component props.
   * @param {Object} props.auth - Auth client for obtaining access tokens.
   * @param {string} [props.locale='en'] - Locale code.
   * @param {Object} props.user - The user record being edited.
   * @param {Function} props.onUserUpdated - Callback invoked with the
   *   updated user after a successful patch.
   */
  constructor(props) {
    super(props);
    this.state = {
      expandLetterAdoptionOverride: null,
      locale: props.locale || 'en',
    };
  }

  /**
   * Resolves whether the adoption override controls should be expanded.
   *
   * @returns {boolean|number|null} Current expanded value.
   */
  expandLetterAdoptionOverride() {
    return this.state.expandLetterAdoptionOverride || this.props.user.letter_adoption_override;
  }

  /**
   * Maps override select input values into persisted override payloads.
   *
   * @param {Event} event
   * @returns {number|null} Parsed override value.
   */
  letterAdoptionOverrideFromEvent(event) {
    const value = event.target.value;
    if (value === 'default') {
      return null;
    }

    return Number(value);
  }

  /**
   * Persists admin override changes for the selected user.
   *
   * @param {Object} payload
   * @returns {Promise<void>} Resolves after the parent is updated.
   */
  async updateUser(payload) {
    const { auth, onUserUpdated, user } = this.props;
    const accessToken = await auth.getAccessToken();
    const response = await axios({
      method: 'PATCH',
      headers: {
        Authorization: 'Bearer '.concat(accessToken),
      },
      url: `${ process.env.REACT_APP_API_URL }/v1/users/${ user.auth0_id }/admin`,
      data: payload,
    });
    onUserUpdated(response.data);
  }

  /**
   * Toggles override controls and clears existing override values.
   *
   * @returns {void}
   */
  handleOverrideLetterAdoptionToggle = () => {
    this.setState({ expandLetterAdoptionOverride: !this.expandLetterAdoptionOverride() });
    this.updateUser({
      letter_adoption_override: null,
    });
  };

  /**
   * Applies a selected letter adoption override value.
   *
   * @param {Event} event
   * @returns {void}
   */
  handleLetterAdoptionOverrideSelect = (event) => {
    const letterAdoptionOverride = this.letterAdoptionOverrideFromEvent(event);
    const payload = {
      letter_adoption_override: letterAdoptionOverride,
    };

    this.updateUser(payload);
  };

  /**
   * Renders override selection UI when overrides are enabled.
   *
   * @returns {JSX.Element|boolean} Selection input or false when hidden.
   */
  renderLetterAdoptionOverrideSelection = () => {
    if (!this.expandLetterAdoptionOverride()) {
      return false;
    }

    const value = this.props.user.letter_adoption_override || 'default';

    return (
      <div style={{ marginTop: '1rem' }}>
        <select
          className='form-control'
          onChange={ this.handleLetterAdoptionOverrideSelect }
          value={ value }
        >
          <option value='default'>(default)</option>
          <option value={ 50000 }>50,000</option>
          <option value={ 100000 }>100,000</option>
          <option value={ 200000 }>200,000</option>
        </select>
      </div>
    );
  };

  /**
   * Renders the overrides panel with a toggle switch for letter adoption
   * override and a dropdown for selecting the override limit.
   *
   * @returns {JSX.Element} The user overrides panel.
   */
  render() {
    return (
      <div className='overrides'>
        <div className='row'>
          <div className='col-9'>
            <label>{ i18n[this.state.locale]['admin.user.letterAdoptionOverride.label'] }</label><br />
            <span>{ i18n[this.state.locale]['admin.user.letterAdoptionOverride.description'] }</span>
          </div>
          <div className='col'>
            <label className='switch float-end'>
              <input checked={ this.expandLetterAdoptionOverride() } onChange={ this.handleOverrideLetterAdoptionToggle } type='checkbox' />
              <span className='slider round' />
            </label>
          </div>
        </div>
        { this.renderLetterAdoptionOverrideSelection() }
      </div>
    );
  }
}

export default AdminUserOverrides;
